/**
 * A sandbox's age, its estimated cost and when it last saw activity (LO-198), and
 * the author's own switch for the emails about it (LO-200).
 *
 * Every figure and every sentence about what it means comes from the kernel's
 * `sandbox_upkeep` on the request and from `GET /notices`; this only puts them
 * into words. The estimate is always called one, because it is list prices
 * applied to what a sandbox is built from and not a bill.
 */

export const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export function daysWord(n) {
  const d = Math.max(0, Math.floor(Number(n) || 0));
  if (d === 0) return "less than a day";
  return `${d} day${d === 1 ? "" : "s"}`;
}

/** The one line the panel shows: age, then the estimate, worded as one. */
export function upkeepLine(upkeep) {
  if (!upkeep || !upkeep.estimate) return null;
  const e = upkeep.estimate;
  const parts = [`Open ${daysWord(upkeep.age_days)}`, `estimated ${money(e.monthly_usd)} a month`];
  if (Number(upkeep.age_days) >= 1 && e.so_far_usd != null) parts.push(`about ${money(e.so_far_usd)} so far`);
  return { text: parts.join(" · "), title: e.basis || "" };
}

const WHAT = { draft: "the draft was saved", qa_run: "a QA run started", read: "you opened it" };

/** When it last saw activity, and what happens when it goes quiet. */
export function activityLine(upkeep) {
  if (!upkeep) return "";
  const what = WHAT[upkeep.last_activity] || "activity";
  const ago = upkeep.idle_days == null ? "" : `, ${daysWord(upkeep.idle_days)} ago`;
  const after = Number(upkeep.idle_chase_after_days) || 0;
  const chase = after
    ? ` After ${daysWord(after)} with nothing new you are reminded by email, and once more at ${daysWord(after * 2)}.`
    : "";
  return `Last activity: ${what}${ago}.${chase} It is never removed on a timer: submitting or cancelling the draft closes it.`;
}

/** The estimate's lines as label and figure, with the shared gateway apart. */
export function estimateRows(estimate) {
  if (!estimate) return [];
  const rows = (estimate.lines || []).map((l) => ({
    key: l.item, label: l.label, figure: `${money(l.monthly_usd)} a month`, basis: l.basis,
  }));
  if (estimate.shared) {
    rows.push({
      key: estimate.shared.item, label: estimate.shared.label,
      figure: `${money(estimate.shared.monthly_usd)} a month, shared`, basis: estimate.shared.basis,
    });
  }
  return rows;
}

/** `{ on, detail }` for one kind of notice in `GET /notices`, or `null`. */
export function noticeSwitch(prefs, kind) {
  const found = (prefs?.kinds || []).find((k) => k.kind === kind);
  return found ? { on: Boolean(found.on), detail: found.detail || "" } : null;
}
